import React, { useState, useEffect, useRef } from "react";
import { PitchFormValues } from "@/PitchFormPage/components/Schema/pitchValidationSchema";
import Swal from "sweetalert2";

interface IntegrationModalProps {
  isOpen: boolean;
  onClose: (data: any) => void;
  integrationType?: "Airtable" | "Google Sheet";
  closeModal:any
}

const IntegrationModal: React.FC<IntegrationModalProps> = ({
  isOpen,
  onClose,
  integrationType = "Airtable",
  closeModal
}) => {
  const [step, setStep] = useState(1);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMapping, setFieldMapping] = useState<{ [key: string]: string }>({});
  const selectRefs = useRef<{ [key: string]: HTMLSelectElement | null }>({});

  const handleFinish = () => {
    if (!isMappingValid()) {
      alert("Please select a value for every checked field.");
      return;
    }
    onClose(fieldMapping);
  }

  useEffect(() => {
    setStep(1);
    setCsvFile(null);
    setCsvHeaders([]);
    setFieldMapping({});
    setSelectedDSPs([]); 
  }, [integrationType, isOpen]);

  const handleClose = () => {
    setFieldMapping({});
    closeModal(false)
  };
   const pitchFields: { key: string; label: string }[] = [
  { key: "artistName", label: "Artist Name" },
  { key: "releaseTitle", label: "Release Name" },
  { key: "releaseDate", label: "Release Date" },
  { key: "configuration", label: "Configuration" },
  { key: "releaseUpc", label: "Release UPC" },
  { key: "focusTrackName", label: "Focus Track Name" },
  { key: "trackIsrc", label: "Track ISRC" },
  { key: "featuredArtists", label: "Featured Artists" },
  { key: "featuredArtistUri", label: "Featured Artist URI" },
  { key: "altTitles", label: "Alt Titles" },
  { key: "shortDescription", label: "Description" },
  { key: "longDescription", label: "Marketing Drivers" },
  { key: "lyrics", label: "Lyrics" },
  { key: "songDescription", label: "Song Description" },
  { key: "mainGenre", label: "Main Genre" },
  { key: "songMood", label: "Song Mood" },
  { key: "language", label: "Language" },
  // spotify
  { key: "dsps.spotify.spotifyUri", label: "Spotify URI" },
  { key: "dsps.spotify.subgenre", label: "Spotify Subgenre" },
  { key: "dsps.spotify.musicCulture", label: "Music Culture" },
  { key: "dsps.spotify.pinToPlaylist", label: "Pin To This Is Playlist" },
  { key: "dsps.spotify.instruments", label: "Instruments" },
  { key: "dsps.spotify.artistHometown", label: "Artist Hometown" },
  { key: "dsps.spotify.artistCurrentTown", label: "Artist Current Town" },
  { key: "dsps.spotify.songStyle", label: "Song Style" },
  // apple
  { key: "dsps.apple.appleId", label: "Apple ID" },
  // tidal
  { key: "dsps.tidal.artistPageLink", label: "TIDAL Page" },
  { key: "dsps.tidal.subgenre", label: "TIDAL Subgenre" },
  { key: "dsps.tidal.country", label: "Country" },
  { key: "dsps.tidal.labelDistributor", label: "Label Distributor" },
  { key: "dsps.tidal.socialHandles", label: "Socials Links" },
  // amazon 
  { key: "dsps.amazon.similarArtists", label: "Similar Artists" },
  { key: "dsps.amazon.addAmazonVoiceIntro", label: "Add Amazon Voice Intro" },
  { key: "dsps.amazon.listenersLocation", label: "Listeners Location" },
  { key: "dsps.amazon.songCategory", label: "Song Category" },
  { key: "dsps.amazon.subgenre", label: "Amazon Subgenre" },
  { key: "dsps.amazon.asin", label: "ASIN" },
  //deezer
  { key: "dsps.deezer.subgenre", label: "Deezer Subgenre" },
  { key: "dsps.deezer.editorialTeam", label: "Deezer Editorial Team" },
  { key: "dsps.deezer.priority", label: "Priority" },
  // soundcloud
  { key: "dsps.soundcloud.soundCloudProfileUrl", label: "SoundCloud Profile URL" },
  { key: "dsps.soundcloud.soundCloudContact", label: "SoundCloud Contact" },
  { key: "dsps.soundcloud.subgenre", label: "SoundCloud Subgenre" },
  { key: "dsps.soundcloud.isPremierProgram", label: "Is Premier Program" },
  { key: "dsps.soundcloud.releaseStage", label: "SoundCloud Release Stage" },
  { key: "dsps.soundcloud.freshMusicFriday", label: "Fresh Music Friday" },
  { key: "dsps.soundcloud.playlistConsideration", label: "Playlist Consideration" },
  { key: "dsps.soundcloud.distributor", label: "Distributor" },
  { key:"dsps.soundcloud.promotionTerritories", label:" Promotion Territories"},
  // audiomack
  { key: "dsps.audiomack.pitchToTrending", label: "Pitch To Audiomack Trending" },
  { key: "dsps.audiomack.subgenre", label: "Audiomack Subgenre" },
  // youtube
  { key: "dsps.youtube.promotionSurface", label: "YouTube Promotion Surface" },
  { key: "dsps.youtube.songDetails", label: "YouTube Release Stage" },
  { key: "dsps.youtube.promotionDate", label: "YouTube Promotion Date" },
  { key: "dsps.youtube.territories", label: "YouTube Territories" },

  // Other fields
  { key: "linkToListen", label: "Link To Listen" },
  { key: "promoPhotos", label: "Promo Photos" },
  { key: "images", label: "Images" },
];

const DSPs = [
  { key: "spotify", label: "Spotify" },
  { key: "apple", label: "Apple" },
  { key: "tidal", label: "TIDAL" },
  { key: "amazon", label: "Amazon" },
  { key: "deezer", label: "Deezer" },
  { key: "soundcloud", label: "SoundCloud" },
  { key: "audiomack", label: "Audiomack" },
  { key: "youtube", label: "YouTube" },
];

  const [selectedDSPs, setSelectedDSPs] = useState<string[]>([]);

  const handleDSPChange = (dspKey: string, checked: boolean) => {
    setSelectedDSPs(prev =>
      checked ? [...prev, dspKey] : prev.filter(k => k !== dspKey)
    );
  };

  const getDSPFromFieldKey = (fieldKey: string) => {
    const match = fieldKey.match(/^dsps\.(\w+)/);
    return match ? match[1] : null;
  };

  const handleFileChange = (file: File | null) => {
    setCsvFile(file);
    if (file) {
    const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const headers = text.split("\n")[0].split(",").map(h => h.trim());
        setCsvHeaders(headers);
      };
      reader.readAsText(file);
    } else {
      setCsvHeaders([]);
    }
  };

  if (!isOpen) return null;

  const Stepper = () => (
    <div className="flex items-center mb-6">
      <div className={`flex items-center ${step === 1 ? "font-bold text-teal-700" : "text-gray-500"}`}>
        <span className="w-6 h-6 flex items-center justify-center rounded-full border border-teal-600 bg-white mr-2">
          1
        </span>
        Upload CSV
      </div>
      <div className="mx-2 text-gray-400">—</div>
      <div className={`flex items-center ${step === 2 ? "font-bold text-teal-700" : "text-gray-500"}`}>
        <span className="w-6 h-6 flex items-center justify-center rounded-full border border-teal-600 bg-white mr-2">
          2
        </span>
        Mapping
      </div>
    </div>
  );

  const isMappingValid = () => {
    // Required fields
    const required = ["artistName", "releaseTitle", "releaseDate"];
    const allCheckedHaveValue = Object.entries(fieldMapping).every(([_, value]) => value !== "");
    const allRequiredPresent = required.every(key => fieldMapping[key] && fieldMapping[key] !== "");
    return allCheckedHaveValue && allRequiredPresent;
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div>
          <p className="text-gray-600 mb-4">Upload your CSV file to connect with Cadence Pitch.</p>
          <input
            type="file"
            accept=".csv"
            className="block w-full text-sm text-gray-700 border border-gray-300 rounded-lg p-2 mb-2"
            onChange={e => handleFileChange(e.target.files?.[0] || null)}
          />
          {csvFile && (
            <div className="mb-2 text-xs text-teal-700 font-semibold">
              Uploaded file: {csvFile.name}
            </div>
          )}
        </div>
      );
    }

    // Mapping step
    const handleMappingChange = (fieldKey: string, header: string) => {
      setFieldMapping(prev => ({ ...prev, [fieldKey]: header }));
    };

    // Group fields by DSP
    const nonDSPFields = pitchFields.filter(f => !getDSPFromFieldKey(f.key));
    const dspFieldsByDSP: { [dsp: string]: { key: string; label: string }[] } = {};
    DSPs.forEach(dsp => {
      dspFieldsByDSP[dsp.key] = pitchFields.filter(f => getDSPFromFieldKey(f.key) === dsp.key);
    });

    return (
      <div className="flex gap-6" style={{ minHeight: 320 }}>
        <div className="flex-1 overflow-y-auto max-h-[400px] pr-2">
          <p className="text-gray-600 mb-4">
            Select DSPs and map your CSV columns to Cadence Pitch fields.
          </p>
          <div className="mb-4 flex gap-4 flex-wrap">
            {DSPs.map(dsp => (
              <label key={dsp.key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedDSPs.includes(dsp.key)}
                  onChange={e => handleDSPChange(dsp.key, e.target.checked)}
                  className="accent-teal-600"
                />
                <span className="font-semibold text-gray-700">{dsp.label}</span>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-8">
            {/* Non-DSP fields */}
            {nonDSPFields.map(({ key, label }) => {
              const isMapped = fieldMapping.hasOwnProperty(key);
              const usedHeaders = Object.entries(fieldMapping)
                .filter(([mappedKey, mappedHeader]) => mappedKey !== key && mappedHeader)
                .map(([_, mappedHeader]) => mappedHeader);

              const availableHeaders = csvHeaders.filter(header => !usedHeaders.includes(header));

              return (
                <div key={key} className="flex items-center py-2 border-b border-gray-100 justify-between">
                  <div>
                    <input
                      type="checkbox"
                      checked={isMapped}
                      onChange={e => {
                        if (e.target.checked) {
                          setFieldMapping(prev => ({ ...prev, [key]: "" }));
                        } else {
                          setFieldMapping(prev => {
                            const copy = { ...prev };
                            delete copy[key];
                            return copy;
                          });
                        }
                      }}
                      className="mr-3 accent-teal-600"
                    />
                    <span className="text-gray-800 font-medium min-w-[160px]">{label}</span>
                  </div>
                  <div className="flex">
                    <span className="mx-3 text-gray-400 text-5xl">&larr;</span>
                    <select
                      ref={el => (selectRefs.current[key] = el)}
                      className="border rounded px-2 py-1 text-gray-700 bg-white min-w-[180px] max-w-[400px] text-base"
                      value={fieldMapping[key] || ""}
                      onChange={e => handleMappingChange(key, e.target.value)}
                      disabled={!isMapped}
                      style={{ width: "100%", maxWidth: "400px" }}
                    >
                      <option value="">Import Field</option>
                      {availableHeaders.map((header, idx) => (
                        <option key={idx} value={header}>{header}</option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}

            {/* DSP fields grouped by DSP with heading */}
            {DSPs.filter(dsp => selectedDSPs.includes(dsp.key)).map(dsp => (
              <React.Fragment key={dsp.key}>
                <div className="font-bold text-lg text-teal-700 mt-6 mb-2">{dsp.label}</div>
                {dspFieldsByDSP[dsp.key].map(({ key, label }) => {
                  const isMapped = fieldMapping.hasOwnProperty(key);
                  const usedHeaders = Object.entries(fieldMapping)
                    .filter(([mappedKey, mappedHeader]) => mappedKey !== key && mappedHeader)
                    .map(([_, mappedHeader]) => mappedHeader);

                  const availableHeaders = csvHeaders.filter(header => !usedHeaders.includes(header));

                  return (
                    <div key={key} className="flex items-center py-2 border-b border-gray-100 justify-between">
                      <div>
                        <input
                          type="checkbox"
                          checked={isMapped}
                          onChange={e => {
                            if (e.target.checked) {
                              setFieldMapping(prev => ({ ...prev, [key]: "" }));
                            } else {
                              setFieldMapping(prev => {
                                const copy = { ...prev };
                                delete copy[key];
                                return copy;
                              });
                            }
                          }}
                          className="mr-3 accent-teal-600"
                        />
                        <span className="text-gray-800 font-medium min-w-[160px]">{label}</span>
                      </div>
                      <div className="flex">
                        <span className="mx-3 text-gray-400 text-5xl">&larr;</span>
                        <select
                          ref={el => (selectRefs.current[key] = el)}
                          className="border rounded px-2 py-1 text-gray-700 bg-white min-w-[180px] max-w-[400px] text-base"
                          value={fieldMapping[key] || ""}
                          onChange={e => handleMappingChange(key, e.target.value)}
                          disabled={!isMapped}
                          style={{ width: "100%", maxWidth: "400px" }}
                        >
                          <option value="">Import Field</option>
                          {availableHeaders.map((header, idx) => (
                            <option key={idx} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleFinishClick = () => {
    const required = ["artistName", "releaseTitle", "releaseDate"];
    const missing = required.filter(key => !fieldMapping[key] || fieldMapping[key] === "");
    if (missing.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Missing Required Fields",
        text: `Please select a value for: ${missing.map(k => pitchFields.find(f => f.key === k)?.label).join(", ")}`,
        timer: 2500,
        showConfirmButton: false,
      });
      const firstMissing = missing[0];
      if (selectRefs.current[firstMissing]) {
        selectRefs.current[firstMissing]?.scrollIntoView({ behavior: "smooth", block: "center" });
        selectRefs.current[firstMissing]?.focus();
      }
      return;
    }
    // Check for other checked fields with no value
    const emptyChecked = Object.entries(fieldMapping).find(([_, value]) => value === "");
    if (emptyChecked) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Mapping",
        text: "Please select a value for every checked field.",
        timer: 2500,
        showConfirmButton: false,
      });
      const key = emptyChecked[0];
      if (selectRefs.current[key]) {
        selectRefs.current[key]?.scrollIntoView({ behavior: "smooth", block: "center" });
        selectRefs.current[key]?.focus();
      }
      return;
    }

    // Set unmapped DSP fields to null for selected DSPs
    let finalMapping:any= { ...fieldMapping };
    DSPs.forEach(dsp => {
      if (selectedDSPs.includes(dsp.key)) {
        pitchFields
          .filter(f => getDSPFromFieldKey(f.key) === dsp.key)
          .forEach(f => {
            if (!finalMapping.hasOwnProperty(f.key)) {
              finalMapping[f.key] = null;
            }
          });
      }
    });

    onClose(finalMapping);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-lg p-12 w-full max-w-5xl relative">
        <button
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-700 text-2xl"
          onClick={handleClose}
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="text-xl font-bold mb-6 text-gray-800">Integrations</h2>
        <Stepper />
        <div className="bg-gray-50 rounded-lg p-4 border relative">
          {renderStepContent()}
        </div>
        <div className="flex justify-between mt-8">
          <div>
            {step === 2 && (
              <button
                className="px-6 py-2 rounded bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300"
                onClick={() => setStep(1)}
              >
                Back
              </button>
            )}
          </div>
          <div>
            {step === 1 && (
              <button
                className={`px-6 py-2 rounded bg-teal-600 text-white font-semibold hover:bg-teal-700 ${!csvFile ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={!csvFile}
                onClick={() => setStep(2)}
              >
                Next: Mapping
              </button>
            )}
            {step === 2 && (
              <button
                className={`px-6 py-2 rounded bg-teal-600 text-white font-semibold hover:bg-teal-700 ${!isMappingValid() ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={false}
                onClick={handleFinishClick}
              >
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationModal;